import { authFetch } from "../../lib/authFetch";
import {
  DaySchedule,
  GroupedWeeklySchedule,
  SCHEDULE_GROUPS,
  StoreCutoff,
  StoreCutoffPayload,
  WEEKDAY_DISPLAY_ORDER,
  WEEKDAY_SHORT,
  WeeklySchedule,
} from "./types";

let localIdSeq = 0;
export function makeLocalId(): string {
  localIdSeq += 1;
  return `pass-${Date.now()}-${localIdSeq}`;
}

/** Recorta "15:00:00" (formato time de Postgres) a "15:00" para <input type="time">. */
export function toInputTime(cutoffTime: string): string {
  const match = String(cutoffTime || "").match(/^(\d{2}:\d{2})/);
  return match ? match[1] : "";
}

export function createEmptyWeeklySchedule(): WeeklySchedule {
  return WEEKDAY_DISPLAY_ORDER.map((weekday) => ({
    weekday,
    enabled: false,
    passes: [],
  }));
}

export function cutoffsToWeeklySchedule(cutoffs: StoreCutoff[]): WeeklySchedule {
  const byDay = new Map<number, StoreCutoff[]>();

  (cutoffs || []).forEach((c) => {
    const weekday = Number(c.weekday);
    if (!byDay.has(weekday)) byDay.set(weekday, []);
    byDay.get(weekday)!.push(c);
  });

  return WEEKDAY_DISPLAY_ORDER.map((weekday) => {
    const rows = (byDay.get(weekday) || [])
      .slice()
      .sort((a, b) => Number(a.window_seq) - Number(b.window_seq));

    return {
      weekday,
      enabled: rows.length > 0,
      passes: rows.map((r) => ({
        localId: makeLocalId(),
        time: toInputTime(r.cutoff_time),
      })),
    };
  });
}

/** Sólo los días habilitados con horario cargado generan filas. Sin fila = no se colecta. */
export function weeklyScheduleToPayload(schedule: WeeklySchedule): StoreCutoffPayload[] {
  const payload: StoreCutoffPayload[] = [];

  schedule.forEach((day) => {
    if (!day.enabled) return;

    let seq = 0;
    day.passes.forEach((pass) => {
      const time = (pass.time || "").trim();
      if (!time) return;
      seq += 1;
      payload.push({
        weekday: day.weekday,
        window_seq: seq,
        cutoff_time: time,
        active: true,
      });
    });
  });

  return payload;
}

export function weeklyScheduleHasAnySchedule(schedule: WeeklySchedule): boolean {
  return weeklyScheduleToPayload(schedule).length > 0;
}

/** Resumen compacto para listados, ej: "L-V · 15:00, SÁB · 12:00" o "Sin colecta configurada". */
export function summarizeWeeklySchedule(schedule: WeeklySchedule): string {
  const signature = (day: DaySchedule): string | null => {
    if (!day.enabled) return null;
    const times = day.passes.map((p) => (p.time || "").trim()).filter(Boolean);
    if (!times.length) return null;
    return times.join("/");
  };

  const days = WEEKDAY_DISPLAY_ORDER.map((weekday) => {
    const day = schedule.find((d) => d.weekday === weekday);
    return { weekday, sig: day ? signature(day) : null };
  });

  const parts: string[] = [];
  let i = 0;
  while (i < days.length) {
    const { sig } = days[i];
    if (!sig) {
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < days.length && days[j + 1].sig === sig) j += 1;

    const startLabel = WEEKDAY_SHORT[days[i].weekday];
    const endLabel = WEEKDAY_SHORT[days[j].weekday];
    const dayRange = i === j ? startLabel : `${startLabel}-${endLabel}`;

    const times = sig.split("/");
    const timeLabel = times.length === 1 ? times[0] : `${times.length} pasadas`;

    parts.push(`${dayRange} · ${timeLabel}`);
    i = j + 1;
  }

  return parts.length ? parts.join(", ") : "Sin colecta configurada";
}

/* ===== Agrupación Lun-Vie / Sáb / Dom ===== */

function daySignature(day: DaySchedule | undefined): string {
  if (!day || !day.enabled) return "off";
  const times = day.passes.map((p) => (p.time || "").trim()).filter(Boolean).sort();
  return times.length ? times.join("/") : "off";
}

/** Lunes a viernes deberían compartir siempre el mismo horario. Si hay datos
 * previos donde no coinciden, hay que avisar antes de agruparlos y sobrescribirlos. */
export function weekdaysGroupIsConsistent(schedule: WeeklySchedule): boolean {
  const sigs = [1, 2, 3, 4, 5].map((weekday) =>
    daySignature(schedule.find((d) => d.weekday === weekday))
  );
  return sigs.every((s) => s === sigs[0]);
}

export function createEmptyGroupedSchedule(): GroupedWeeklySchedule {
  return SCHEDULE_GROUPS.map((g) => ({ ...g, enabled: false, passes: [] }));
}

/** Representa cada grupo con el primer weekday que lo compone (lunes para Lun-Vie). */
export function weeklyScheduleToGrouped(schedule: WeeklySchedule): GroupedWeeklySchedule {
  return SCHEDULE_GROUPS.map((g) => {
    const rep = schedule.find((d) => d.weekday === g.weekdays[0]);
    return {
      ...g,
      enabled: rep?.enabled ?? false,
      passes: (rep?.passes ?? []).map((p) => ({ ...p })),
    };
  });
}

/** Expande cada grupo a sus weekdays reales (Lun-Vie -> 1,2,3,4,5) para persistir con el contrato actual. */
export function groupedScheduleToWeekly(grouped: GroupedWeeklySchedule): WeeklySchedule {
  const byWeekday = new Map<number, DaySchedule>();

  grouped.forEach((g) => {
    g.weekdays.forEach((weekday) => {
      byWeekday.set(weekday, {
        weekday,
        enabled: g.enabled,
        passes: g.passes.map((p) => ({ ...p, localId: `${p.localId}-d${weekday}` })),
      });
    });
  });

  return WEEKDAY_DISPLAY_ORDER.map(
    (weekday) => byWeekday.get(weekday) ?? { weekday, enabled: false, passes: [] }
  );
}

export function groupedScheduleToPayload(grouped: GroupedWeeklySchedule): StoreCutoffPayload[] {
  return weeklyScheduleToPayload(groupedScheduleToWeekly(grouped));
}

/** Convierte lo que vino del backend a la vista agrupada, señalando si Lun-Vie
 * ya no coincidían entre sí (dato legacy) para poder avisar antes de guardar. */
export function cutoffsToGroupedSchedule(cutoffs: StoreCutoff[]): {
  schedule: GroupedWeeklySchedule;
  weekdaysInconsistent: boolean;
} {
  const weekly = cutoffsToWeeklySchedule(cutoffs);
  return {
    schedule: weeklyScheduleToGrouped(weekly),
    weekdaysInconsistent: !weekdaysGroupIsConsistent(weekly),
  };
}

/* ===== API ===== */

type StoreCutoffsResponse = {
  ok?: boolean;
  error?: string;
  store_id?: number;
  cutoffs?: StoreCutoff[];
};

export async function fetchStoreCutoffs(
  token: string | null,
  storeId: number | string
): Promise<StoreCutoff[]> {
  const response = await authFetch(`/v2/store-cutoffs/${storeId}`, { token });
  const json: StoreCutoffsResponse = await response.json().catch(() => ({}));

  if (!response.ok || json?.ok === false) {
    throw new Error(json?.error || "No se pudieron cargar los horarios de colecta");
  }

  return json.cutoffs || [];
}

export async function saveStoreCutoffs(
  token: string | null,
  storeId: number | string,
  cutoffs: StoreCutoffPayload[]
): Promise<StoreCutoff[]> {
  const response = await authFetch(`/v2/store-cutoffs/${storeId}`, {
    method: "PUT",
    token,
    json: true,
    body: JSON.stringify({ cutoffs }),
  });

  const json: StoreCutoffsResponse = await response.json().catch(() => ({}));

  if (!response.ok || json?.ok === false) {
    throw new Error(json?.error || "No se pudieron guardar los horarios de colecta");
  }

  return json.cutoffs || [];
}
